import {
  Component,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { PostTradeService } from '../../post-trade.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormGroup, FormGroup, FormControl } from '@angular/forms';
import { UniversalSearchService } from '../../../universal-search/services/universal-search.service';
import { Observable, Subject } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {
  AccountInfo,
  AccountResult,
  HashResult,
} from '../../../models/post-trade-models';

@Component({
  selector: 'app-account-grouping-dialog',
  templateUrl: './account-grouping-dialog.component.html',
  styleUrls: ['./account-grouping-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AccountGroupingDialogComponent implements OnInit, OnDestroy {
  dialogRef =
    inject<MatDialogRef<AccountGroupingDialogComponent>>(MatDialogRef);
  private ptSvc = inject(PostTradeService);
  private usServ = inject(UniversalSearchService);
  private snacky = inject(MatSnackBar);
  private route = inject(Router);
  private location = inject(Location);
  data = inject(MAT_DIALOG_DATA);

  unsubscribe = new Subject();
  recommendedAccounts;
  selectedAccount = '';
  loading = false;
  tableView = true;
  groupCreationForm: FormGroup;
  unitOptions = [];
  unitControl = new FormControl<any>('');
  accountGroupForm: FormGroup;
  repCodeOptions = [];
  repCodeControl = new FormControl<any>('');
  filteredOptions: Observable<any>;
  AccountIDControl = new FormControl<any>('');
  HashedSSNControl = new FormControl<any>('');
  valueHashed = false;
  createView = false;

  ngOnInit() {
    if (!this.data.createGroup) {
      if (!this.data.searchView) {
        this.getSuggestedAccounts();
      }
    } else {
      this.createView = true;
      const formGroup = {};
      formGroup['description'] = new FormControl();
      formGroup['unit'] = new FormControl();
      this.groupCreationForm = new UntypedFormGroup(formGroup);
      this.getFilteredOptions();
    }

    if (this.data.searchView) {
      this.tableView = false;
      const formGroup = {};
      formGroup['AccountID'] = new FormControl();
      formGroup['OwnerFirstName'] = new FormControl();
      formGroup['OwnerLastName'] = new FormControl();
      formGroup['HashedSSN'] = new FormControl();
      formGroup['RepCode'] = new FormControl();
      this.accountGroupForm = new UntypedFormGroup(formGroup);
      this.getFilteredOptions();
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  getSuggestedAccounts() {
    this.loading = true;
    const queryData = {};
    if (this.accountGroupForm) {
      queryData['search'] = {};
      const formKeys = Object.keys(this.accountGroupForm['value']);
      formKeys.map(keyName => {
        if (this.accountGroupForm['value'][keyName]) {
          queryData['search'][keyName] = {
            searchString: this.accountGroupForm['value'][keyName],
          };
        }
      });
    }
    this.ptSvc.searchAccount(queryData).subscribe({
      next: (resp: AccountInfo[]) => {
        this.recommendedAccounts = resp;
        this.loading = false;
      },
      error: err => {
        this.snacky.open(
          `Whoops something went wrong, please try again later!`,
          'Close',
          {
            duration: 3000,
          }
        );
        console.error(err);
      },
    });
  }

  linkAccount() {
    if (
      this.groupCreationForm &&
      this.groupCreationForm.value.unit &&
      this.groupCreationForm.value.description
    ) {
      this.createAccountGroup();
    } else {
      this.ptSvc
        .linkAccount(this.data.groupId, this.selectedAccount)
        .subscribe({
          next: () => {
            this.snacky.open(`Account Linked!`, 'Close', {
              duration: 3000,
            });
            this.dialogRef.close();
          },
          error: err => {
            this.snacky.open(
              `Whoops something went wrong, please try again later!`,
              'Close',
              {
                duration: 3000,
              }
            );
            console.error(err);
          },
        });
    }
  }

  selectAccount(accountId) {
    if (this.selectedAccount === accountId) {
      this.selectedAccount = '';
    } else {
      this.selectedAccount = accountId;
    }
  }

  viewAccountGroup(account) {
    window.open(
      `${this.location.prepareExternalUrl('')}account-grouping/${
        account.GroupID
      }`,
      '_blank'
    );
  }

  accountsNotSelected() {
    return !Object.values(this.selectedAccount).some(value => value);
  }

  toggleView() {
    if (!this.tableView) {
      this.getSuggestedAccounts();
    }
    this.tableView = !this.tableView;
  }

  getFilteredOptions() {
    this.repCodeControl.valueChanges
      .pipe(
        takeUntil(this.unsubscribe),
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe(value => {
        this.usServ.repCodeSearch(value).subscribe(resp => {
          const newUnitList = resp.names.map(rep => ({
            value: rep.repCode,
            display: rep.name,
          }));
          this.repCodeOptions = newUnitList;
        });
      });

    this.unitControl.valueChanges
      .pipe(
        takeUntil(this.unsubscribe),
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe(value => {
        this.usServ.unitSearch(value).subscribe(resp => {
          const newUnitList = resp.names.map(rep => ({
            value: rep.id,
            display: rep.name,
          }));
          this.unitOptions = newUnitList;
        });
      });
  }

  onBlur(ev) {
    if (this.repCodeControl.value === '') {
      if (this.accountGroupForm.controls['unit']) {
        this.accountGroupForm.controls['unit'].setValue('');
      } else {
        this.accountGroupForm.controls['RepCode'].setValue('');
      }
    }

    if (!ev.target.value.includes(':')) {
      if (this.accountGroupForm.controls['unit']) {
        this.accountGroupForm.controls['unit'].setValue(ev.target.value);
      } else {
        this.accountGroupForm.controls['RepCode'].setValue(ev.target.value);
      }
    }
  }

  changeSelection(ev) {
    this.repCodeControl.setValue(ev.option.getLabel());

    this.accountGroupForm.controls['RepCode'].setValue(ev.option.value);
  }

  changeUnit(ev) {
    this.unitControl.setValue(ev.option.getLabel());
    if (this.groupCreationForm.controls['unit']) {
      this.groupCreationForm.controls['unit'].setValue(ev.option.value);
    } else {
      this.groupCreationForm.controls['RepCode'].setValue(ev.option.value);
    }
  }

  proceedAndLinkAccounts() {
    this.createView = false;
    this.tableView = false;
    const formGroup = {};
    formGroup['AccountID'] = new FormControl();
    formGroup['OwnerFirstName'] = new FormControl();
    formGroup['OwnerLastName'] = new FormControl();
    formGroup['HashedSSN'] = new FormControl();
    formGroup['RepCode'] = new FormControl();
    this.accountGroupForm = new UntypedFormGroup(formGroup);
  }

  createAccountGroup() {
    const { description, unit } = this.groupCreationForm.value;

    this.ptSvc
      .createAccountGroup({ description, unitid: unit })
      .subscribe((resp: AccountResult) => {
        if (resp.result) {
          this.ptSvc.linkAccount(resp.result, this.selectedAccount).subscribe({
            next: () => {
              this.ptSvc
                .makePrimaryAccount(resp.result!, this.selectedAccount)
                .subscribe({
                  next: () => {
                    this.snacky.open(
                      `Account group created, account linked & assigned as primary`,
                      'Close',
                      {
                        duration: 3000,
                      }
                    );
                    this.dialogRef.close();
                    this.route.navigate([`/account-grouping/${resp.result}`]);
                  },
                  error: err => {
                    this.snacky.open(
                      `Whoops something went wrong, please try again later!`,
                      'Close',
                      {
                        duration: 3000,
                      }
                    );
                    console.error(err);
                  },
                });
            },
            error: err => {
              this.snacky.open(
                `Whoops something went wrong, please try again later!`,
                'Close',
                {
                  duration: 3000,
                }
              );
              console.error(err);
            },
          });
        }
      });
  }

  hashInput(ev, groupField) {
    if (ev.target.value) {
      this.ptSvc
        .getHashString(ev.target.value)
        .subscribe((resp: HashResult) => {
          this.valueHashed = true;
          if (resp.resultString) {
            this.accountGroupForm.controls[groupField].setValue(
              resp.resultString
            );
            this[groupField + 'Control'].setValue(resp.resultString);
            setTimeout(() => {
              this.valueHashed = false;
            }, 750);
          }
        });
    }
  }
}
